import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Clock, ChefHat, Sparkles } from "lucide-react";
import api, { resolveImg } from "../lib/api";
import SmartImage from "../components/SmartImage";

const CATEGORIES = [
  { id: "all", name: "All Recipes" },
  { id: "diffuser-blends", name: "Diffuser Blends" },
  { id: "roller-bottles", name: "Roller Bottles" },
  { id: "sprays", name: "Sprays & Mists" },
  { id: "candles", name: "Candles" },
  { id: "soaps", name: "Soaps & Bath" },
  { id: "cleaning", name: "Natural Cleaning" },
];

export default function Recipes() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const category = searchParams.get("category") || "all";

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    setLoading(true);
    const params = {};
    if (category !== "all") params.category = category;
    api
      .get("/recipes", { params })
      .then((res) => setRecipes(res.data))
      .catch(() => setRecipes([]))
      .finally(() => setLoading(false));
  }, [category]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const setCategory = (id) => {
    const np = new URLSearchParams(searchParams);
    if (id === "all") np.delete("category");
    else np.set("category", id);
    setSearchParams(np);
  };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-12 lg:py-20" data-testid="recipes-page">
      <div className="mb-12 max-w-3xl">
        <p className="eyebrow mb-3">Recipes from the apothecary</p>
        <h1 className="font-serif text-5xl lg:text-6xl text-[var(--ink-forest)] tracking-tight">
          Make something
          <span className="italic text-[var(--terracotta)]"> beautiful.</span>
        </h1>
        <p className="mt-5 text-[var(--muted-fg)] leading-relaxed text-lg">
          Free recipes for diffusers, rollers, sprays, candles, and more — using oils and supplies
          you&apos;ll find on our shelves. Click any recipe to shop the ingredients in one tap.
        </p>
      </div>

      {/* Category pills */}
      <div className="flex gap-2 flex-wrap mb-10" data-testid="recipe-categories">
        {CATEGORIES.map((c) => (
          <button
            key={c.id}
            onClick={() => setCategory(c.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              category === c.id
                ? "bg-[var(--terracotta)] text-white"
                : "bg-[var(--bg-surface)] border border-[var(--border)] text-[var(--ink-forest)] hover:border-[var(--terracotta)]"
            }`}
            data-testid={`recipe-cat-${c.id}`}
          >
            {c.name}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="aspect-[4/5] bg-[var(--muted)] rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : recipes.length === 0 ? (
        <div className="text-center py-20">
          <p className="font-serif text-2xl text-[var(--ink-forest)]">No recipes here yet.</p>
          <p className="mt-2 text-[var(--muted-fg)]">Try another category — more recipes added often.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {recipes.map((r) => (
            <RecipeCard key={r.id} recipe={r} />
          ))}
        </div>
      )}
    </div>
  );
}

export function RecipeCard({ recipe }) {
  return (
    <Link
      to={`/recipes/${recipe.slug}`}
      className="group block bg-[var(--bg-surface)] border border-[var(--border)] rounded-3xl overflow-hidden hover:shadow-[0_16px_48px_rgba(26,54,38,0.08)] transition-all duration-300"
      data-testid={`recipe-card-${recipe.slug}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-[var(--muted)]">
        <SmartImage
          src={resolveImg(recipe.image_url)}
          alt={recipe.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        {recipe.featured && (
          <div className="absolute top-3 left-3 bg-[var(--terracotta)] text-white text-xs font-semibold px-3 py-1 rounded-full inline-flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Favorite
          </div>
        )}
      </div>
      <div className="p-5">
        <p className="eyebrow text-[10px]">{categoryLabel(recipe.category)}</p>
        <h3 className="mt-2 font-serif text-xl text-[var(--ink-forest)] group-hover:text-[var(--terracotta)] transition-colors leading-tight">
          {recipe.title}
        </h3>
        <p className="mt-2 text-sm text-[var(--muted-fg)] line-clamp-2">{recipe.short_description}</p>
        <div className="mt-4 flex items-center gap-4 text-xs text-[var(--muted-fg)]">
          <span className="inline-flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" /> {recipe.time_minutes} min
          </span>
          <span className="inline-flex items-center gap-1">
            <ChefHat className="w-3.5 h-3.5" /> {recipe.difficulty}
          </span>
        </div>
      </div>
    </Link>
  );
}

function categoryLabel(c) {
  const cat = CATEGORIES.find((x) => x.id === c);
  return cat ? cat.name : c;
}
